import type Sentry from '@/shared/lib/sentry'
import type { TFunction } from 'i18next'

import { lazy, Suspense } from 'react'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { AlertTriangleIcon, ArrowLeftIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { ErrorContentMessage } from '@/shared/components/errors'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Spinner } from '@/shared/components/ui/spinner'

const Runs = lazy(() =>
	import('./Runs').then((module) => ({
		default: module.Runs,
	}))
)
const fallback = (
	props: React.ComponentProps<Sentry.FallbackRender>,
	t: TFunction
) => {
	if (props.error instanceof AxiosError) {
		return (
			<Empty>
				<EmptyHeader>
					<EmptyMedia
						variant='icon'
						className='bg-destructive/20 text-destructive'
					>
						<AlertTriangleIcon />
					</EmptyMedia>
					<EmptyDescription>
						{props.error.response?.data?.detail}
					</EmptyDescription>
				</EmptyHeader>
				<EmptyContent>
					<div className='flex items-center gap-2'>
						<Button size='xs' variant='link'>
							<Link to='/p/whatif/runs'>
								<ArrowLeftIcon />
								{t('app.back_to_previous_page', {
									defaultValue: 'Back to previous page',
								})}
							</Link>
						</Button>
					</div>
				</EmptyContent>
			</Empty>
		)
	}

	return <ErrorContentMessage {...props} />
}
export function Page() {
	const { t } = useTranslation()

	const params = useParams({
		from: '/_app/p/whatif/runs/$scenarioRunSetId/summary',
	})
	const { reset } = useQueryErrorResetBoundary()

	return (
		<ErrorBoundary onReset={reset} fallback={(props) => fallback(props, t)}>
			<Suspense
				fallback={
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<Spinner />
							</EmptyMedia>
							<EmptyDescription>
								{t('loading', { defaultValue: 'Loading...' })}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				}
			>
				<Runs scenarioRunSetId={params.scenarioRunSetId!} />
			</Suspense>
		</ErrorBoundary>
	)
}
